/**
 * The working set a tablet carries offline, and the device-side check made against it
 * (PROJECT_SPEC.md §8.2 step 2, §8.6; URS-8.6-001, OFF-009).
 *
 * §8.2: "Client validates against the LOCALLY CACHED, HASHED MBR SNAPSHOT using the SAME rule code the server
 * runs." That sentence is the whole design here: `prevalidate` calls the very functions the server's
 * ExecutionService calls (`validateEntry`, `routeFacts`, the STEP_EXECUTION machine and its GUARDS), over the
 * replica instead of the database. It adds no rule of its own, so the device cannot be stricter or looser than
 * the server by construction. It is still only a courtesy: the server re-decides every command on replay and
 * may always reject (§8.3, N6, N7). Facts the device cannot know (equipment state, hold-time windows, the
 * operator's override permission) are treated as satisfied here and left to the server.
 */
import { isFormulaCode } from "../calc/derive.js";
import { findParameter, outOfSpecPolicy, validateEntry } from "../capture/validate.js";
import type { MbrContent } from "../mbr/content.js";
import { routeFacts } from "../mbr/routing.js";
import { GUARDS } from "../state/guards.js";
import { transition } from "../state/machine.js";
import { STEP_EXECUTION, type StepEvent, type StepState } from "../state/machines.js";

/** One batch as the server last described it. Read-only on the device; never edited, only replaced. */
export interface WorkingSetBatch {
  batchId: string;
  batchNumber: string;
  status: string;
  /** The batch's event-stream version when this was cut: the cursor a delta is computed against. */
  streamVersion: number;
  /** SHA-256 of the canonical effective MBR content; the device re-derives it before trusting `content`. */
  contentHash: string;
  content: MbrContent;
  /** Every execution of every step, latest and earlier. */
  steps: { stepId: string; instanceOrdinal: number; state: string }[];
  /** Current (not superseded) values. `numeric` marks the ones branch conditions may read (WF-006). */
  values: { stepId: string; parameterCode: string; value: string; numeric: boolean }[];
  /** decision step id -> the step its taken branch leads to. */
  pins: Record<string, string>;
  /** Whether the signed-in operator's training for this batch's recipe is current (WF-016). */
  trainingCurrent: boolean;
}

export interface WorkingSetLot {
  id: string;
  lotNumber: string;
  materialCode: string;
  status: string;
  expiryDate: string | null;
}
export interface WorkingSetEquipment {
  id: string;
  code: string;
  name: string;
  operationalState: string;
  calibrationValidUntil: string | null;
}

/** What the server sends: everything that changed since the versions the device said it holds. */
export interface WorkingSetDelta {
  /** Server time the set was cut. Display only; the record's time is always taken at replay (N5). */
  asOf: string;
  /** The site's IANA zone: a typed wall time is read in it, exactly as the server reads it (TIME-008). */
  siteTz: string;
  batches: WorkingSetBatch[];
  /** Batches the device holds that are still current: nothing to send. */
  unchanged: string[];
  /** Batches that are no longer open work (finished, closed, aborted): drop from the replica. */
  removed: string[];
  lots: WorkingSetLot[];
  equipment: WorkingSetEquipment[];
}

export interface WorkingSet {
  asOf: string;
  siteTz: string;
  batches: Record<string, WorkingSetBatch>;
  lots: WorkingSetLot[];
  equipment: WorkingSetEquipment[];
}

export const emptyWorkingSet: WorkingSet = { asOf: "", siteTz: "UTC", batches: {}, lots: [], equipment: [] };

/** Lots and equipment are small and change independently of batches, so they are replaced whole each time. */
export function applyDelta(prev: WorkingSet, d: WorkingSetDelta): WorkingSet {
  const batches = { ...prev.batches };
  for (const id of d.removed) delete batches[id];
  for (const b of d.batches) batches[b.batchId] = b;
  return { asOf: d.asOf, siteTz: d.siteTz, batches, lots: d.lots, equipment: d.equipment };
}

/** The `known` cursor a device sends so the server can answer with a delta (batch id -> stream version). */
export const knownVersions = (ws: WorkingSet): Record<string, number> =>
  Object.fromEntries(Object.values(ws.batches).map((b) => [b.batchId, b.streamVersion]));

export type Prevalidation = { ok: true } | { ok: false; code: string; message: string };

const ok: Prevalidation = { ok: true };
const refuse = (code: string, message: string): Prevalidation => ({ ok: false, code, message });

/** The device's own authority claim is never used: the server re-reads the principal's permissions (N6). */
const DEVICE_PERMISSIONS: ReadonlySet<string> = new Set(["batch.step.perform"]);

const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);

function latestStates(ws: WorkingSetBatch): Record<string, string> {
  const best = new Map<string, { n: number; state: string }>();
  for (const s of ws.steps) {
    const cur = best.get(s.stepId);
    if (!cur || s.instanceOrdinal > cur.n) best.set(s.stepId, { n: s.instanceOrdinal, state: s.state });
  }
  return Object.fromEntries([...best].map(([k, v]) => [k, v.state]));
}

function entryContext(ws: WorkingSetBatch, code: string, cfg: Record<string, unknown>) {
  const cross = (cfg["crossCheck"] as { ref?: string } | undefined)?.ref;
  if (!cross && cfg["unique"] !== true) return {};
  const recorded: Record<string, string> = {};
  for (const v of ws.values)
    if (v.parameterCode === code || v.parameterCode === cross) recorded[v.parameterCode] = v.value;
  return { recorded, prior: ws.values.filter((v) => v.parameterCode === code).map((v) => v.value) };
}

function checkRecordValue(
  ws: WorkingSetBatch,
  p: Record<string, unknown>,
  opts: { now: Date; siteTz: string },
): Prevalidation {
  const stepId = str(p["stepId"]);
  const code = str(p["parameterCode"]);
  const step = ws.content.procedure.steps.find((s) => s.id === stepId);
  // same order as ExecutionService.record: calculated first, then the parameter, then the value, then the gates
  if (step && isFormulaCode(step, code))
    return refuse(
      "CALCULATED_FIELD_READ_ONLY",
      `${code} is calculated from other entries and cannot be typed or overwritten. Correct the entry it is calculated from.`,
    );
  const parameter = findParameter(ws.content, stepId, code);
  if (!parameter)
    return refuse(
      "UNKNOWN_PARAMETER",
      `${code} is not a parameter of step ${stepId} in the record this batch is executing.`,
    );
  const checked = validateEntry(parameter, str(p["value"]), {
    onOutOfSpec: outOfSpecPolicy(ws.content, stepId),
    context: entryContext(ws, code, parameter.config),
    now: opts.now,
    siteTz: opts.siteTz,
  });
  if (!checked.ok) return refuse(checked.rejection.code, checked.rejection.message);
  const keyed = str(p["entryMethod"], "KEYED") === "KEYED";
  if (parameter.config["scanRequired"] === true && keyed && !p["scanOverrideReason"])
    return refuse(
      "SCAN_REQUIRED",
      `${parameter.label} must be scanned. Typing it needs a reason and an override permission.`,
    );
  if (checked.blocked && !p["oosOverrideReason"])
    return refuse(
      "OUT_OF_SPEC_BLOCKED",
      `${parameter.label} is outside its specification and this step is configured to stop rather than continue. Raise a deviation to proceed.`,
    );
  return ok;
}

function checkStepAction(ws: WorkingSetBatch, p: Record<string, unknown>): Prevalidation {
  const stepId = str(p["stepId"]);
  const event = str(p["event"]) as StepEvent;
  const step = ws.content.procedure.steps.find((s) => s.id === stepId);
  if (!step)
    return refuse("UNKNOWN_STEP", `Step ${stepId} is not part of the record this batch is executing.`);
  const ordinal = Number(p["instanceOrdinal"] ?? 1);
  const known = ws.steps.find((s) => s.stepId === stepId && s.instanceOrdinal === ordinal);
  if (!known && ordinal > 1)
    return refuse(
      "INSTANCE_NOT_OPENED",
      `Execution ${ordinal} of ${stepId} has not been opened; a repeat is opened by re-executing the step with a reason.`,
    );
  const numericValues = Object.fromEntries(
    ws.values.filter((v) => v.numeric).map((v) => [v.parameterCode, v.value]),
  );
  const routing = routeFacts(ws.content, latestStates(ws), numericValues, ws.pins).get(stepId);
  const r = transition(STEP_EXECUTION, (known?.state ?? "NOT_STARTED") as StepState, event, {
    permissions: DEVICE_PERMISSIONS,
    guards: GUARDS,
    facts: {
      reason: str(p["reason"]),
      stepIsOptional: step.mandatory === false,
      prereqStepsComplete: routing?.prereqStepsComplete ?? false,
      parallelBranchesJoined: routing?.parallelBranchesJoined ?? false,
      routingReason: routing?.reason ?? "",
      trainingCurrent: ws.trainingCurrent,
      // not knowable on the device; the server reads equipment and hold windows from its own tables
      equipmentReady: true,
      holdTimeWithinWindow: true,
      supervisorApproved: true,
    },
  });
  // the server answers every refused transition with this one code (EventStore -> AppendError)
  return r.ok ? ok : refuse("FORBIDDEN_TRANSITION", r.message);
}

/** WF-020: nothing is executed on a batch that is not in progress. */
const NOT_EXECUTING = (status: string) =>
  refuse(
    "BATCH_NOT_EXECUTING",
    `This batch is ${status.toLowerCase().replace(/_/g, " ")}; execution can only be recorded while it is in progress. Everything recorded so far is kept.`,
  );

/**
 * Would the server, holding this replica's facts, refuse this command? A `false` here is a refusal made on the
 * device with the server's own code and wording; a `true` promises nothing.
 */
export function prevalidate(
  ws: WorkingSetBatch,
  commandType: string,
  payload: Record<string, unknown>,
  opts: { now: Date; siteTz: string },
): Prevalidation {
  if (commandType === "StepAction") {
    if (ws.status !== "IN_PROGRESS") return NOT_EXECUTING(ws.status);
    return checkStepAction(ws, payload);
  }
  if (commandType === "RecordValue") return checkRecordValue(ws, payload, opts);
  return ok;
}
